/**
 * QR crew-signup link management (admin) + the public join flow.
 */

import { api, setTokens } from './client';
import type { User } from './auth';

// ---------------------------------------------------------------------------
// Admin side
// ---------------------------------------------------------------------------

export interface CrewInviteLink {
  id: string;
  label: string;
  blurb: string | null;
  url: string;
  expiresAt: string | null;
  revokedAt: string | null;
  useCount: number;
  createdAt: string;
  active: boolean;
}

export async function createCrewInvite(data: {
  label: string;
  blurb?: string;
  expiresAt?: string;
}): Promise<CrewInviteLink> {
  const response = await api.post<{ link: CrewInviteLink }>('/crew-invites', data);
  return response.data.link;
}

export async function listCrewInvites(): Promise<CrewInviteLink[]> {
  const response = await api.get<{ links: CrewInviteLink[] }>('/crew-invites');
  return response.data.links;
}

export async function revokeCrewInvite(id: string): Promise<void> {
  await api.delete(`/crew-invites/${id}`);
}

// ---------------------------------------------------------------------------
// Public join side
// ---------------------------------------------------------------------------

export interface JoinInfo {
  orgName: string;
  blurb: string;
  expiresAt: string | null;
}

export interface JoinResponse {
  message: string;
  joinedOrgSlug: string;
  user: User;
  accessToken: string;
  refreshToken: string;
}

export async function getJoinInfo(token: string): Promise<JoinInfo> {
  const response = await api.get<JoinInfo>(`/join/${token}`);
  return response.data;
}

function storeTokens(data: JoinResponse): JoinResponse {
  setTokens(data.accessToken, data.refreshToken);
  return data;
}

export async function joinWithPassword(
  token: string,
  data: { firstName: string; lastName: string; email: string; password: string }
): Promise<JoinResponse> {
  const response = await api.post<JoinResponse>(`/join/${token}`, data);
  return storeTokens(response.data);
}

export async function joinWithGoogle(token: string, idToken: string): Promise<JoinResponse> {
  const response = await api.post<JoinResponse>(`/join/${token}/google`, { idToken });
  return storeTokens(response.data);
}

/** One-tap join for an already-signed-in user (fresh tokens carry the new org). */
export async function joinAsMember(token: string): Promise<JoinResponse> {
  const response = await api.post<JoinResponse>(`/join/${token}/member`, {});
  return storeTokens(response.data);
}
